import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, ShoppingCart, Package, TrendingDown, Building2, Wallet } from "lucide-react";
import { toast } from "sonner";

import { api, apiError, fmtDate, fmtInr } from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import ActionsNow from "../components/ActionsNow";
import WhiteboardBanner from "../components/WhiteboardBanner";

/**
 * Procurement Officer landing dashboard.
 *
 *   Landing focus: vendor price-comparison table (biggest saving first).
 *   Sidebar rolls up open PRs + submitted requisitions.
 *   Actions:
 *     · approve / order a PR (inline)
 *     · raise a "review vendor" flag against the costliest vendor for an item
 *
 *   Owner/Head can also open this page (they see the same view).
 */
export default function ProcurementDashboardPage() {
    const [priceData, setPriceData] = useState(null);
    const [queue, setQueue] = useState(null);
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(null);

    async function refresh() {
        setBusy(true);
        try {
            const [pd, q] = await Promise.all([
                api.get("/procurement/price-comparison", { params: { months: 6, min_vendors: 2 } }),
                api.get("/procurement/queue"),
            ]);
            setPriceData(pd.data || { items: [] });
            setQueue(q.data || { open_purchase_requests: [], submitted_requisitions: [] });
        } catch (e) {
            toast.error(apiError(e));
        } finally { setBusy(false); }
    }
    useEffect(() => { refresh(); }, []);

    async function approvePR(pr_id) {
        try {
            await api.patch(`/purchase-requests/${pr_id}`, { status: "APPROVED" });
            toast.success("Purchase request approved");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function orderPR(pr_id) {
        try {
            await api.patch(`/purchase-requests/${pr_id}`, { status: "ORDERED" });
            toast.success("Marked ordered");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function approveReq(rid) {
        try {
            const { data } = await api.post(`/requisitions/${rid}/approve`);
            toast.success(`Requisition approved — ${data.purchase_requests?.length || 0} PR(s) created`);
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function flagVendor(vendor_id, item_key, vendor_name) {
        const reason = window.prompt(`Review flag for ${vendor_name} on "${item_key}"?\n\nEnter your reason (audit-logged):`);
        if (!reason || !reason.trim()) return;
        try {
            await api.post(`/procurement/vendors/${vendor_id}/review-flag`, { reason: reason.trim(), item_key });
            toast.success(`Flag raised on ${vendor_name}`);
        } catch (e) { toast.error(apiError(e)); }
    }

    const items = priceData?.items || [];
    const totalPotentialSaving = useMemo(
        () => items.reduce((s, r) => s + (r.potential_saving_monthly_inr || 0), 0),
        [items]
    );

    return (
        <div className="mx-auto max-w-7xl px-4 py-6 space-y-6" data-testid="procurement-dashboard">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-[#1E3355]" data-testid="page-title">Procurement dashboard</h1>
                    <p className="text-sm text-gray-600 mt-1">Vendor price comparison, purchase requests, and requisition queue.</p>
                </div>
                <div className="text-right hidden sm:block">
                    <div className="text-xs text-gray-500">Potential monthly saving surfaced</div>
                    <div className="text-xl font-bold text-emerald-600" data-testid="total-potential-saving">
                        {fmtInr(totalPotentialSaving) || "—"}
                    </div>
                </div>
            </header>

            <WhiteboardBanner />
            <ActionsNow />

            {/* Queue summary strip */}
            <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="queue-summary">
                <QueueStat icon={<ShoppingCart className="w-5 h-5" />} label="PRs — Requested" value={queue?.counts?.prs_requested ?? "—"} testid="stat-prs-requested" />
                <QueueStat icon={<Package className="w-5 h-5" />} label="PRs — Approved (to order)" value={queue?.counts?.prs_approved ?? "—"} testid="stat-prs-approved" />
                <QueueStat icon={<Building2 className="w-5 h-5" />} label="Requisitions — Submitted" value={queue?.counts?.requisitions_submitted ?? "—"} testid="stat-reqs-submitted" />
            </section>

            {/* Price comparison table */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-100" data-testid="price-comparison-section">
                <div className="p-4 border-b flex items-center justify-between">
                    <h2 className="font-semibold text-[#1E3355] flex items-center gap-2"><TrendingDown className="w-4 h-4" /> Vendor price comparison (last 6 months)</h2>
                    <button onClick={refresh} disabled={busy} className="text-xs text-gray-500 hover:text-gray-800" data-testid="refresh-btn">
                        {busy ? "Refreshing…" : "Refresh"}
                    </button>
                </div>
                {items.length === 0 ? (
                    <div className="p-6">
                        <EmptyState
                            icon="🧾"
                            title="No cross-vendor overlap yet"
                            subtitle="Once the same item is purchased from 2+ vendors, comparison rows appear here with an indicative monthly saving figure."
                        />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="price-comparison-table">
                            <thead className="bg-gray-50 text-left">
                                <tr>
                                    <th className="p-3 font-medium text-gray-700">Item</th>
                                    <th className="p-3 font-medium text-gray-700">Cheapest</th>
                                    <th className="p-3 font-medium text-gray-700">Costliest</th>
                                    <th className="p-3 font-medium text-gray-700 text-right">Spread</th>
                                    <th className="p-3 font-medium text-gray-700 text-right">Potential saving / mo</th>
                                    <th className="p-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => (
                                    <PriceRow
                                        key={row.item_key}
                                        row={row}
                                        expanded={expanded === row.item_key}
                                        onToggle={() => setExpanded(expanded === row.item_key ? null : row.item_key)}
                                        onFlagVendor={flagVendor}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Submitted requisitions */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-100" data-testid="submitted-requisitions-section">
                <div className="p-4 border-b">
                    <h2 className="font-semibold text-[#1E3355] flex items-center gap-2"><Building2 className="w-4 h-4" /> Submitted requisitions</h2>
                </div>
                {(!queue?.submitted_requisitions?.length) ? (
                    <div className="p-6">
                        <EmptyState icon="📋" title="No submitted requisitions" subtitle="Nothing waiting for approval right now." />
                    </div>
                ) : (
                    <ul className="divide-y">
                        {queue.submitted_requisitions.map((r) => (
                            <li key={r.id} className="p-4 flex items-center justify-between" data-testid={`req-row-${r.id}`}>
                                <div>
                                    <div className="font-medium text-[#1E3355]">{r.site_name} · {r.month}</div>
                                    <div className="text-xs text-gray-500">{r.line_count} line(s) · submitted {r.submitted_at ? fmtDate(r.submitted_at) : "—"}</div>
                                </div>
                                <button
                                    onClick={() => approveReq(r.id)}
                                    className="px-3 py-1.5 rounded-md bg-[#1E3355] hover:bg-[#26406b] text-white text-xs font-semibold"
                                    data-testid={`approve-req-${r.id}`}
                                >Approve</button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Open purchase requests */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-100" data-testid="open-prs-section">
                <div className="p-4 border-b">
                    <h2 className="font-semibold text-[#1E3355] flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Open purchase requests</h2>
                </div>
                {(!queue?.open_purchase_requests?.length) ? (
                    <div className="p-6">
                        <EmptyState icon="🛒" title="No open purchase requests" subtitle="Requests appear here as sites raise them." />
                    </div>
                ) : (
                    <ul className="divide-y">
                        {queue.open_purchase_requests.map((p) => (
                            <li key={p.id} className="p-4 flex items-center justify-between" data-testid={`pr-row-${p.id}`}>
                                <div>
                                    <div className="font-medium text-[#1E3355]">{p.item_name || p.item_snapshot?.name || "(unnamed)"}</div>
                                    <div className="text-xs text-gray-500">
                                        {p.site_name} · qty {p.qty_suggested ?? p.required ?? "—"} · status <span className="font-medium">{p.status}</span> · created {p.created_at ? fmtDate(p.created_at) : "—"}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {p.status === "REQUESTED" && (
                                        <button onClick={() => approvePR(p.id)} className="px-3 py-1.5 rounded-md bg-[#1E3355] hover:bg-[#26406b] text-white text-xs font-semibold" data-testid={`approve-pr-${p.id}`}>Approve</button>
                                    )}
                                    {(p.status === "REQUESTED" || p.status === "APPROVED") && (
                                        <button onClick={() => orderPR(p.id)} className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold" data-testid={`order-pr-${p.id}`}>Mark ordered</button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}


function QueueStat({ icon, label, value, testid }) {
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3" data-testid={testid}>
            <div className="w-10 h-10 rounded-full bg-[#1E3355]/10 text-[#1E3355] flex items-center justify-center">{icon}</div>
            <div>
                <div className="text-xs text-gray-500">{label}</div>
                <div className="text-xl font-bold text-[#1E3355]">{value}</div>
            </div>
        </div>
    );
}


function PriceRow({ row, expanded, onToggle, onFlagVendor }) {
    const badge = row.overpay_flag ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700";
    return (
        <>
            <tr className="border-t hover:bg-gray-50 cursor-pointer" onClick={onToggle} data-testid={`price-row-${row.item_key}`}>
                <td className="p-3">
                    <div className="font-medium text-[#1E3355]">{row.item_name}</div>
                    <div className="text-xs text-gray-500">{row.vendors.length} vendors</div>
                </td>
                <td className="p-3">
                    <div className="font-semibold text-emerald-700">{fmtInr(row.cheapest_rate)}</div>
                    <div className="text-xs text-gray-500">{row.cheapest_vendor}</div>
                </td>
                <td className="p-3">
                    <div className="font-semibold text-red-700">{fmtInr(row.costliest_rate)}</div>
                    <div className="text-xs text-gray-500">{row.costliest_vendor}</div>
                </td>
                <td className={`p-3 text-right`}>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${badge}`}>{row.spread_pct}%</span>
                </td>
                <td className="p-3 text-right font-semibold text-[#1E3355]">
                    {row.potential_saving_monthly_inr > 0 ? fmtInr(row.potential_saving_monthly_inr) : "—"}
                </td>
                <td className="p-3 text-right">
                    <ArrowRight className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
                </td>
            </tr>
            {expanded && (
                <tr className="bg-gray-50">
                    <td colSpan={6} className="p-4">
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Per-vendor breakdown</div>
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-gray-500 text-left">
                                    <th className="pb-1">Vendor</th>
                                    <th className="pb-1">Latest rate</th>
                                    <th className="pb-1">Latest date</th>
                                    <th className="pb-1">Avg rate</th>
                                    <th className="pb-1">Qty (3-mo)</th>
                                    <th className="pb-1">Lines</th>
                                    <th className="pb-1"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {row.vendors.map((v) => (
                                    <tr key={v.vendor_id} className="border-t">
                                        <td className="py-1.5 font-medium">{v.vendor_name}</td>
                                        <td className="py-1.5">{fmtInr(v.latest_rate)}</td>
                                        <td className="py-1.5">{v.latest_date ? fmtDate(v.latest_date) : "—"}</td>
                                        <td className="py-1.5">{fmtInr(v.avg_rate)}</td>
                                        <td className="py-1.5">{v.qty_last_3mo}</td>
                                        <td className="py-1.5">{v.line_count}</td>
                                        <td className="py-1.5 text-right">
                                            {v.latest_rate === row.costliest_rate && row.overpay_flag && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onFlagVendor(v.vendor_id, row.item_key, v.vendor_name); }}
                                                    className="text-red-600 hover:underline text-xs font-semibold"
                                                    data-testid={`flag-vendor-${v.vendor_id}`}
                                                >
                                                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                                                    Review vendor
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </td>
                </tr>
            )}
        </>
    );
}
